/* Shared shell enhancement. Shopify remains authoritative for every cart write. */
(() => {
  if (customElements.get('ix-commerce-panels') || !window.HTMLDialogElement) return;
  class IndexesCommercePanels extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.labels = JSON.parse(this.querySelector('[data-panel-labels]').textContent);
      this.root = this.dataset.root || '/';
      this.tab = 'cart';
      const on = (target, name, fn) => target.addEventListener(name, fn, { signal: this.events.signal });
      on(document, 'click', event => {
        const trigger = event.target.closest('[data-ix-panel]');
        if (!trigger || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button) return;
        event.preventDefault(); this.open(trigger.dataset.ixPanel, trigger);
      });
      on(this, 'click', event => {
        if (event.target.closest('[data-panel-close]')) this.close();
        const tab = event.target.closest('[data-panel-tab]');
        if (tab) this.selectTab(tab.dataset.panelTab);
        if (event.target.closest('[data-cart-retry]')) this.refresh();
        const quantity = event.target.closest('[data-cart-key]');
        if (quantity) this.change(quantity.dataset.cartKey, Number(quantity.dataset.quantity));
        if (event.target.closest('[data-cart-review][aria-disabled=true]')) event.preventDefault();
        if (event.target === this.active) {
          const r = this.active.getBoundingClientRect();
          if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.close();
        }
      });
      on(this, 'keydown', event => {
        const tab = event.target.closest('[data-panel-tab]');
        if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 'cart' : event.key === 'End' ? 'recent' : tab.dataset.panelTab === 'cart' ? 'recent' : 'cart';
        this.selectTab(next); this.querySelector(`[data-panel-tab="${next}"]`).focus();
      });
      for (const dialog of this.querySelectorAll('dialog')) {
        on(dialog, 'cancel', event => { event.preventDefault(); this.close(); });
      }
      on(document, 'indexes:cart-added', () => { this.pulseCart(); this.open('cart', document.activeElement); });
      on(window, 'pageshow', event => { if (event.persisted && this.active?.id === 'ix-panel-cart') this.refresh(); });
      const search = this.querySelector('[data-ix-predictive-input]');
      on(search, 'input', () => { this.querySelector('[data-search-discovery]').hidden = search.value.trim().length >= 2; });
      document.querySelectorAll('[data-ix-panel]').forEach(t => {
        t.setAttribute('aria-controls', `ix-panel-${t.dataset.ixPanel}`);
        t.setAttribute('aria-haspopup', 'dialog'); t.setAttribute('aria-expanded', 'false');
      });
      this.noticeStorageKey = 'indexes-store-notifications-read-v1';
      this.syncNotices();
      on(window, 'storage', event => { if (event.key === this.noticeStorageKey || event.key === null) this.syncNotices(); });
      document.querySelectorAll('[data-ix-panel="notifications"]').forEach(button => { button.hidden = false; });
      this.rememberProduct();
      document.documentElement.classList.add('ix-panels-ready');
    }
    disconnectedCallback() {
      this.close(true); this.events?.abort(); this.events = null;
      this.readRequest?.abort(); this.recentRequest?.abort(); clearTimeout(this.closeTimer); clearTimeout(this.pulseTimer);
      document.querySelectorAll('.ix-cart-pulse').forEach(node => node.classList.remove('ix-cart-pulse'));
      document.documentElement.classList.remove('ix-panels-ready');
    }
    open(name, trigger) {
      const dialog = this.querySelector(`#ix-panel-${name}`);
      if (!dialog) return;
      if (this.active === dialog && !this.closing) return;
      if (this.active) this.close(true);
      this.trigger = trigger; this.active = dialog;
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      dialog.classList.remove('is-closing'); dialog.showModal();
      document.querySelectorAll(`[data-ix-panel="${name}"]`).forEach(t => t.setAttribute('aria-expanded', 'true'));
      if (name === 'search') dialog.querySelector('input[type=search]').focus({ preventScroll: true });
      if (name === 'notifications') this.markNoticesRead();
      if (name === 'cart') { this.selectTab('cart'); this.refresh(); }
    }
    close(immediate = false) {
      if (this.closing && !immediate) return;
      clearTimeout(this.closeTimer);
      const dialog = this.active;
      if (!dialog) return;
      const finish = () => {
        dialog.close(); dialog.classList.remove('is-closing'); this.active = null; this.closing = false;
        dialog.style.removeProperty('--ix-panel-close-from'); dialog.style.removeProperty('--ix-panel-close-opacity');
        document.documentElement.style.overflow = this.previousOverflow || '';
        document.querySelectorAll('[data-ix-panel]').forEach(t => t.setAttribute('aria-expanded', 'false'));
        dialog.querySelector('[data-ix-predictive-wrap]')?.dispatchEvent(new Event('indexes:search-close'));
        if (!immediate && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      };
      if (immediate || matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
      else {
        const current = getComputedStyle(dialog);
        dialog.style.setProperty('--ix-panel-close-from', current.transform);
        dialog.style.setProperty('--ix-panel-close-opacity', current.opacity);
        this.closing = true; dialog.classList.add('is-closing');
        this.closeTimer = setTimeout(finish, 300);
      }
    }
    noticeKeys() {
      return [...this.querySelectorAll('[data-ix-notice]')].map(node => {
        const text = node.textContent.trim() + (node.querySelector('a')?.getAttribute('href') || '');
        let hash = 2166136261;
        for (let i = 0; i < text.length; i++) hash = Math.imul(hash ^ text.charCodeAt(i), 16777619);
        return (hash >>> 0).toString(36);
      });
    }
    syncNotices() {
      try {
        const saved = JSON.parse(localStorage.getItem(this.noticeStorageKey) || '[]');
        this.readNotices = new Set(Array.isArray(saved) ? saved.filter(key => typeof key === 'string') : []);
      } catch (_) { this.readNotices ||= new Set(); }
      this.updateNoticeBadges();
    }
    updateNoticeBadges() {
      const unread = this.noticeKeys().filter(key => !this.readNotices.has(key)).length;
      document.querySelectorAll('[data-ix-notification-count]').forEach(badge => {
        badge.textContent = String(unread); badge.hidden = unread === 0;
      });
    }
    markNoticesRead() {
      this.noticeKeys().forEach(key => this.readNotices.add(key));
      try { localStorage.setItem(this.noticeStorageKey, JSON.stringify([...this.readNotices].slice(-100))); } catch (_) {}
      this.updateNoticeBadges();
    }
    pulseCart() {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      clearTimeout(this.pulseTimer);
      const triggers = document.querySelectorAll('[data-ix-panel="cart"]');
      triggers.forEach(node => node.classList.add('ix-cart-pulse'));
      this.pulseTimer = setTimeout(() => triggers.forEach(node => node.classList.remove('ix-cart-pulse')), 650);
    }
    selectTab(name) {
      this.tab = name;
      for (const tab of this.querySelectorAll('[data-panel-tab]')) {
        const selected = tab.dataset.panelTab === name;
        tab.setAttribute('aria-selected', String(selected)); tab.tabIndex = selected ? 0 : -1;
      }
      this.querySelector('#ix-panel-cart-content').hidden = name !== 'cart';
      this.querySelector('#ix-panel-recent-content').hidden = name !== 'recent';
      this.querySelector('[data-cart-summary]').hidden = name !== 'cart' || !this.cart?.item_count;
      if (name === 'recent') this.loadRecent();
    }
    busy(value) {
      this.querySelector('[data-cart-items]').setAttribute('aria-busy', String(value));
      this.querySelectorAll('[data-cart-key]').forEach(b => { b.disabled = value; });
      this.querySelector('[data-cart-review]').setAttribute('aria-disabled', String(value || this.failed));
    }
    status(message, retry = false) {
      this.querySelector('[data-cart-status]').textContent = message;
      this.querySelector('[data-cart-retry]').hidden = !retry;
    }
    async refresh() {
      if (this.mutating) return;
      this.readRequest?.abort(); const request = this.readRequest = new AbortController();
      this.busy(true); this.status(this.labels.loading);
      try {
        const response = await fetch(this.root + 'cart.js', { cache: 'no-store', signal: AbortSignal.any([request.signal, AbortSignal.timeout(12000)]), headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('cart');
        const cart = await response.json();
        if (request !== this.readRequest || !this.isConnected) return;
        this.failed = false; this.renderCart(cart); this.status('');
      } catch (error) {
        if (request.signal.aborted || !this.isConnected) return;
        this.failed = true; this.status(this.labels.error, true);
      } finally { if (request === this.readRequest && !this.mutating) this.busy(false); }
    }
    async change(key, quantity) {
      if (this.mutating || !Number.isInteger(quantity) || quantity < 0) return;
      this.mutating = true; this.readRequest?.abort(); this.busy(true); this.status(this.labels.loading);
      let failed = false;
      try {
        const response = await fetch(this.root + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }), signal: AbortSignal.timeout(12000) });
        if (!response.ok) throw new Error('change');
        const cart = await response.json();
        if (!this.isConnected) return;
        this.failed = false; this.renderCart(cart); this.status(this.labels.updated);
      } catch (_) { failed = true; }
      finally { this.mutating = false; this.busy(false); }
      // An uncertain mutation is never replayed. Re-read the server before enabling review.
      if (failed && this.isConnected) { await this.refresh(); this.status(this.labels.error, true); }
    }
    node(tag, className, text) {
      const node = document.createElement(tag); if (className) node.className = className;
      if (text != null) node.textContent = text; return node;
    }
    money(cents) {
      try { return new Intl.NumberFormat(document.documentElement.lang || 'ar', { style: 'currency', currency: this.cart?.currency || this.dataset.currency }).format(cents / 100); }
      catch (_) { return (cents / 100).toFixed(2) + ' ' + this.dataset.currency; }
    }
    safeLink(path) {
      try { const url = new URL(path, location.origin); return url.origin === location.origin ? url.pathname + url.search : this.root + 'collections/all'; }
      catch (_) { return this.root + 'collections/all'; }
    }
    image(source) {
      if (!source) return null;
      try {
        const url = new URL(source, location.origin);
        if (url.protocol !== 'https:' || (url.origin !== location.origin && url.hostname !== 'cdn.shopify.com')) return null;
        url.searchParams.set('width', '200'); const image = this.node('img', 'ix-panel-line__image');
        image.src = url.href; image.alt = ''; image.width = 92; image.height = 110; image.loading = 'lazy'; return image;
      } catch (_) { return null; }
    }
    renderCart(cart) {
      this.cart = cart;
      const focused = document.activeElement;
      const focusKey = focused?.dataset.cartKey;
      const focusIndex = focusKey ? [...focused.parentElement.querySelectorAll('button')].indexOf(focused) : -1;
      document.querySelectorAll('[data-ix-cart-count]').forEach(b => { b.textContent = cart.item_count; b.hidden = cart.item_count === 0; });
      const target = this.querySelector('[data-cart-items]'); target.replaceChildren();
      for (const item of cart.items) {
        const row = this.node('article', 'ix-panel-line');
        const picture = this.node('a'); picture.href = this.safeLink(item.url); picture.tabIndex = -1; picture.setAttribute('aria-hidden', 'true');
        const img = this.image(item.image); if (img) picture.append(img); row.append(picture);
        const detail = this.node('div'); const title = this.node('a', 'ix-panel-line__title', item.product_title); title.href = this.safeLink(item.url); detail.append(title);
        if (item.variant_title && item.variant_title !== 'Default Title') detail.append(this.node('small', '', item.variant_title));
        for (const [name, value] of Object.entries(item.properties || {})) if (!name.startsWith('_') && value) detail.append(this.node('small', '', name + ': ' + value));
        if (item.selling_plan_allocation?.selling_plan?.name) detail.append(this.node('small', '', item.selling_plan_allocation.selling_plan.name));
        detail.append(this.node('strong', '', item.original_line_price === 0 && item.final_line_price === 0 ? this.labels.ask_price : this.money(item.final_line_price)));
        const controls = this.node('div', 'ix-panel-line__controls'); const qty = this.node('div', 'ix-panel-quantity');
        const button = (text, quantity, label, className = '') => {
          const b = this.node('button', className, text); b.type = 'button'; b.dataset.cartKey = item.key; b.dataset.quantity = quantity;
          b.setAttribute('aria-label', label + ': ' + item.product_title); return b;
        };
        qty.append(button('−', Math.max(0, item.quantity - 1), this.labels.quantity + ' ' + (item.quantity - 1)), this.node('span', '', item.quantity), button('+', item.quantity + 1, this.labels.quantity + ' ' + (item.quantity + 1)));
        const remove = button(this.labels.remove, 0, this.labels.remove, 'ix-panel-line__remove');
        const removeIcon = this.querySelector('[data-cart-remove-icon]');
        if (removeIcon) remove.replaceChildren(removeIcon.content.cloneNode(true));
        controls.append(qty, remove); detail.append(controls); row.append(detail); target.append(row);
      }
      this.querySelector('[data-cart-empty]').hidden = cart.item_count > 0;
      const unknown = cart.items.some(item => item.original_line_price === 0 && item.final_line_price === 0);
      this.querySelector('[data-cart-total]').textContent = cart.items.length && cart.items.every(item => item.original_line_price === 0 && item.final_line_price === 0) ? this.labels.ask_price : this.money(cart.total_price);
      const subtotalLabel = this.querySelector('[data-cart-total-label]');
      if (subtotalLabel) subtotalLabel.textContent = unknown ? this.labels.partial : this.labels.subtotal;
      document.dispatchEvent(new Event('indexes:cart-refreshed'));
      this.querySelector('[data-cart-summary]').hidden = !cart.item_count || this.tab !== 'cart';
      if (focusKey && this.active?.id === 'ix-panel-cart') {
        const candidates = [...target.querySelectorAll('[data-cart-key]')].filter(b => b.dataset.cartKey === focusKey);
        const restore = focused.classList.contains('ix-panel-line__remove') ? candidates.at(-1) : candidates[focusIndex];
        // Enable before restoring focus; the subsequent busy update preserves mutation locking.
        (restore || this.querySelector('[data-panel-tab="cart"]')).focus({ preventScroll: true });
      }
    }
    rememberProduct() {
      try {
        let handles = JSON.parse(sessionStorage.getItem('indexes_recent_handles_v1') || '[]');
        if (!Array.isArray(handles)) handles = [];
        const match = location.pathname.match(/\/products\/([^/]+)/);
        const current = match ? decodeURIComponent(match[1]) : null;
        if (current) handles = [current, ...handles.filter(h => h !== current)];
        this.recent = handles.filter(h => typeof h === 'string' && h.length < 250 && !/[/?#]/.test(h)).slice(0, 8);
        sessionStorage.setItem('indexes_recent_handles_v1', JSON.stringify(this.recent));
      } catch (_) { this.recent = []; }
    }
    async loadRecent() {
      this.recentRequest?.abort(); const request = this.recentRequest = new AbortController();
      const target = this.querySelector('[data-recent-items]'); target.replaceChildren();
      const empty = this.querySelector('[data-recent-empty]'); empty.hidden = false;
      const results = await Promise.allSettled(this.recent.map(async handle => {
        const response = await fetch(this.root + 'products/' + encodeURIComponent(handle) + '.js', { signal: AbortSignal.any([request.signal, AbortSignal.timeout(10000)]), cache: 'no-store' });
        if (!response.ok) throw new Error('product'); return response.json();
      }));
      if (request.signal.aborted || !this.isConnected) return;
      for (const result of results) {
        if (result.status !== 'fulfilled') continue;
        const product = result.value; const row = this.node('a', 'ix-panel-line'); row.href = this.safeLink(product.url || this.root + 'products/' + product.handle);
        const img = this.image(product.featured_image); if (img) row.append(img); else row.append(this.node('span'));
        const detail = this.node('div'); detail.append(this.node('span', 'ix-panel-line__title', product.title), this.node('strong', '', product.price > 0 ? this.money(product.price) : this.labels.ask_price)); row.append(detail); target.append(row);
      }
      empty.hidden = target.childElementCount > 0;
    }
  }
  customElements.define('ix-commerce-panels', IndexesCommercePanels);
})();
